/**
 * 解析曲面重合配合的最终残差校验。
 *
 * 文件职责：
 * 1. 校验 SOLIDWORKS Coincident Mate 中点/线/圆边与圆柱、圆锥、球面的真实重合关系；
 * 2. 与求解器共用 `StandardMateSurfaceCoincidentGeometry` 中的几何公式，避免求解和校验语义漂移；
 * 3. 只返回布尔残差结果，不修改 Mate Definition、组件 transform 或 UI 状态。
 */

import {
  AssemblyMateType,
  MateAlignment,
  type MateEntityRef,
} from '../definition';
import {
  resolveAxisGeometry,
  resolveConeGeometry,
  resolvePointGeometry,
  resolveSphereGeometry,
} from './geometry/StandardMateGeometryAccess';
import {
  calculateAxisAxisDistance,
  calculatePointAxisDistance,
  isCircularEdgeEntity,
  isConeEntity,
  isCylinderEntity,
  isDirectionAligned,
  isLinearAxisEntity,
  isSphereEntity,
  resolveAxisRadius,
  resolveConeAxialDistanceForEdgeRadius,
  validatePointOnCone,
} from './geometry/StandardMateSurfaceCoincidentGeometry';

/**
 * 校验点是否落在圆边上。
 *
 * @param first 第一实体。
 * @param second 第二实体。
 * @param epsilon 容差。
 * @returns 可校验时返回布尔值；不属于该语义时返回 null。
 */
function validatePointCircularEdgeCoincident(
  first: MateEntityRef,
  second: MateEntityRef,
  epsilon: number,
): boolean | null {
  const firstPoint = resolvePointGeometry(first);
  const secondPoint = resolvePointGeometry(second);
  const firstAxis = resolveAxisGeometry(first);
  const secondAxis = resolveAxisGeometry(second);
  const point = firstPoint ?? secondPoint;
  const circle = firstPoint ? secondAxis : firstAxis;
  const circleEntity = firstPoint ? second : first;
  if (!point || !circle || !isCircularEdgeEntity(circleEntity)) {
    return null;
  }

  const radius = resolveAxisRadius(circle);
  if (radius === null) {
    return null;
  }
  const axialDistance = Math.abs(point.point.clone().sub(circle.origin).dot(circle.direction));
  return axialDistance <= epsilon
    && Math.abs(calculatePointAxisDistance(point.point, circle) - radius) <= epsilon;
}

/**
 * 校验点与解析曲面重合。
 *
 * @param first 第一实体。
 * @param second 第二实体。
 * @param epsilon 容差。
 * @returns 可校验时返回布尔值；不属于该语义时返回 null。
 */
function validatePointSurfaceCoincident(
  first: MateEntityRef,
  second: MateEntityRef,
  epsilon: number,
): boolean | null {
  const firstPoint = resolvePointGeometry(first);
  const secondPoint = resolvePointGeometry(second);
  const firstAxis = resolveAxisGeometry(first);
  const secondAxis = resolveAxisGeometry(second);
  const firstSphere = resolveSphereGeometry(first);
  const secondSphere = resolveSphereGeometry(second);
  const firstCone = resolveConeGeometry(first);
  const secondCone = resolveConeGeometry(second);

  if (firstPoint && secondAxis && isCylinderEntity(second)) {
    const radius = resolveAxisRadius(secondAxis);
    return radius === null
      ? null
      : Math.abs(calculatePointAxisDistance(firstPoint.point, secondAxis) - radius) <= epsilon;
  }
  if (secondPoint && firstAxis && isCylinderEntity(first)) {
    const radius = resolveAxisRadius(firstAxis);
    return radius === null
      ? null
      : Math.abs(calculatePointAxisDistance(secondPoint.point, firstAxis) - radius) <= epsilon;
  }
  if (firstPoint && secondSphere && isSphereEntity(second)) {
    return Math.abs(firstPoint.point.distanceTo(secondSphere.center) - secondSphere.radius) <= epsilon;
  }
  if (secondPoint && firstSphere && isSphereEntity(first)) {
    return Math.abs(secondPoint.point.distanceTo(firstSphere.center) - firstSphere.radius) <= epsilon;
  }
  if (firstPoint && secondCone && isConeEntity(second)) {
    return validatePointOnCone(firstPoint.point, secondCone, epsilon);
  }
  if (secondPoint && firstCone && isConeEntity(first)) {
    return validatePointOnCone(secondPoint.point, firstCone, epsilon);
  }

  return null;
}

/**
 * 校验线与圆柱面重合。
 *
 * @param first 第一实体。
 * @param second 第二实体。
 * @param alignment 对齐方式。
 * @param epsilon 长度容差。
 * @param angleEpsilon 角度容差。
 * @returns 可校验时返回布尔值；不属于该语义时返回 null。
 */
function validateLineCylinderCoincident(
  first: MateEntityRef,
  second: MateEntityRef,
  alignment: MateAlignment | undefined,
  epsilon: number,
  angleEpsilon: number,
): boolean | null {
  const firstAxis = resolveAxisGeometry(first);
  const secondAxis = resolveAxisGeometry(second);
  if (!firstAxis || !secondAxis) {
    return null;
  }

  if (isCylinderEntity(first) && isLinearAxisEntity(second)) {
    const radius = resolveAxisRadius(firstAxis);
    return radius === null
      ? null
      : isDirectionAligned(secondAxis.direction, firstAxis.direction, alignment, angleEpsilon)
        && Math.abs(calculateAxisAxisDistance(firstAxis, secondAxis, epsilon) - radius) <= epsilon;
  }
  if (isLinearAxisEntity(first) && isCylinderEntity(second)) {
    const radius = resolveAxisRadius(secondAxis);
    return radius === null
      ? null
      : isDirectionAligned(secondAxis.direction, firstAxis.direction, alignment, angleEpsilon)
        && Math.abs(calculateAxisAxisDistance(firstAxis, secondAxis, epsilon) - radius) <= epsilon;
  }

  return null;
}

/**
 * 校验圆边与圆柱面重合。
 *
 * @param first 第一实体。
 * @param second 第二实体。
 * @param alignment 对齐方式。
 * @param epsilon 长度容差。
 * @param angleEpsilon 角度容差。
 * @returns 可校验时返回布尔值；不属于该语义时返回 null。
 */
function validateCircularEdgeCylinderCoincident(
  first: MateEntityRef,
  second: MateEntityRef,
  alignment: MateAlignment | undefined,
  epsilon: number,
  angleEpsilon: number,
): boolean | null {
  const firstAxis = resolveAxisGeometry(first);
  const secondAxis = resolveAxisGeometry(second);
  if (!firstAxis || !secondAxis) {
    return null;
  }

  if (
    (isCylinderEntity(first) && isCircularEdgeEntity(second))
    || (isCircularEdgeEntity(first) && isCylinderEntity(second))
  ) {
    const firstRadius = resolveAxisRadius(firstAxis);
    const secondRadius = resolveAxisRadius(secondAxis);
    return firstRadius !== null
      && secondRadius !== null
      && Math.abs(firstRadius - secondRadius) <= epsilon
      && isDirectionAligned(secondAxis.direction, firstAxis.direction, alignment, angleEpsilon)
      && calculateAxisAxisDistance(firstAxis, secondAxis, epsilon) <= epsilon;
  }

  return null;
}

/**
 * 校验圆边与圆锥面重合。
 *
 * @param first 第一实体。
 * @param second 第二实体。
 * @param alignment 对齐方式。
 * @param epsilon 长度容差。
 * @param angleEpsilon 角度容差。
 * @returns 可校验时返回布尔值；不属于该语义时返回 null。
 */
function validateCircularEdgeConeCoincident(
  first: MateEntityRef,
  second: MateEntityRef,
  alignment: MateAlignment | undefined,
  epsilon: number,
  angleEpsilon: number,
): boolean | null {
  const firstAxis = resolveAxisGeometry(first);
  const secondAxis = resolveAxisGeometry(second);
  const firstCone = resolveConeGeometry(first);
  const secondCone = resolveConeGeometry(second);
  if (!firstAxis || !secondAxis) {
    return null;
  }

  const cone = firstCone ?? secondCone;
  const edge = firstCone ? secondAxis : firstAxis;
  if (!cone || !(firstCone ? isCircularEdgeEntity(second) : isCircularEdgeEntity(first))) {
    return null;
  }

  const edgeRadius = resolveAxisRadius(edge);
  const expectedAxialDistance = edgeRadius === null
    ? null
    : resolveConeAxialDistanceForEdgeRadius(cone, edgeRadius);
  if (expectedAxialDistance === null) {
    return null;
  }

  const axialDistance = edge.origin.clone().sub(cone.apex).dot(cone.direction);
  return isDirectionAligned(edge.direction, cone.direction, alignment, angleEpsilon)
    && calculatePointAxisDistance(edge.origin, cone) <= epsilon
    && Math.abs(axialDistance - expectedAxialDistance) <= epsilon;
}

/**
 * 校验两个圆锥面重合。
 *
 * @param first 第一实体。
 * @param second 第二实体。
 * @param alignment 对齐方式。
 * @param epsilon 长度容差。
 * @param angleEpsilon 角度容差。
 * @returns 可校验时返回布尔值；不属于该语义时返回 null。
 */
function validateConeConeCoincident(
  first: MateEntityRef,
  second: MateEntityRef,
  alignment: MateAlignment | undefined,
  epsilon: number,
  angleEpsilon: number,
): boolean | null {
  const firstCone = resolveConeGeometry(first);
  const secondCone = resolveConeGeometry(second);
  if (!firstCone || !secondCone) {
    return null;
  }
  return Math.abs(firstCone.halfAngle - secondCone.halfAngle) <= epsilon
    && firstCone.apex.distanceTo(secondCone.apex) <= epsilon
    && isDirectionAligned(secondCone.direction, firstCone.direction, alignment, angleEpsilon);
}

/**
 * 校验解析曲面 Coincident Mate。
 *
 * @param mate 当前配合定义。
 * @param epsilon 长度容差。
 * @param angleEpsilon 角度容差。
 * @returns 可校验时返回布尔值；不属于该语义时返回 null。
 */
export function validateSurfaceCoincidentMate(
  mate: {
    readonly mateType: AssemblyMateType;
    readonly firstEntity: MateEntityRef;
    readonly secondEntity: MateEntityRef;
    readonly alignment?: MateAlignment;
  },
  epsilon: number,
  angleEpsilon: number,
): boolean | null {
  if (mate.mateType !== AssemblyMateType.Coincident) {
    return null;
  }

  return validatePointSurfaceCoincident(mate.firstEntity, mate.secondEntity, epsilon)
    ?? validatePointCircularEdgeCoincident(mate.firstEntity, mate.secondEntity, epsilon)
    ?? validateLineCylinderCoincident(mate.firstEntity, mate.secondEntity, mate.alignment, epsilon, angleEpsilon)
    ?? validateCircularEdgeCylinderCoincident(mate.firstEntity, mate.secondEntity, mate.alignment, epsilon, angleEpsilon)
    ?? validateCircularEdgeConeCoincident(mate.firstEntity, mate.secondEntity, mate.alignment, epsilon, angleEpsilon)
    ?? validateConeConeCoincident(mate.firstEntity, mate.secondEntity, mate.alignment, epsilon, angleEpsilon);
}
